import React, { useState, useEffect } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import "./BlocksSection.css";
import LevelSwitcher from "./LevelSwitcher";
import BigOInfo from "./BigOInfo";

const BlocksSection = ({
  areBlocksVisible,
  blocks,
  blockHighlightClass,
  highlightedBlock,
  addToSolution,
  showHintButton,
  hintUsed,
  fetchHint,
  onHintClick,
  setHintUsed,
  level,
  handleLevelChange,
  renderSolutionTabs,
  solutionCompleted,
  solutions,
  activeSolutionIndex,
  problem,
  codeSnippet,
  resetWorkspace,
}) => {
  const [showBigOView, setShowBigOView] = useState(false);
  const [loadingHint, setLoadingHint] = useState(false);

  // NEW: Legend modal state
  const [showLegend, setShowLegend] = useState(false);

  const currentSolution = solutions?.[activeSolutionIndex];
  const isOptimal = currentSolution?.isOptimal;
  const timeComplexity = currentSolution?.timeComplexity || "Unknown";
  const spaceComplexity = currentSolution?.spaceComplexity || "Unknown";

  useEffect(() => {
    if (solutionCompleted) {
      setShowBigOView(true);
    }
  }, [solutionCompleted]);

  // Close legend on Escape
  useEffect(() => {
    if (!showLegend) return;
    const onKey = (e) => e.key === "Escape" && setShowLegend(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showLegend]);

  const handleHintClick = async () => {
    setLoadingHint(true);
    await new Promise((res) => setTimeout(res, 10000));
    await onHintClick();
    setLoadingHint(false);
  };

  const handleResetBlocks = () => {
    resetWorkspace();
  };

  return (
    <>
      <div className="blocks-sticky-header">
        <div className="left-block">
          <h2 className="block-library-header">Blocks</h2>
        </div>

        <div className="center-block"></div>

        <div className="right-block">
          {/* NEW: Info (legend) button */}
          <button
            className="info-icon-button"
            title="Block colors legend"
            aria-label="Block colors legend"
            onClick={() => setShowLegend(true)}
          >
            i
          </button>

          {showHintButton && (
            <button
              onClick={handleHintClick}
              disabled={hintUsed || loadingHint}
              className={`hint-button ${hintUsed || loadingHint ? "disabled" : ""}`}
            >
              {hintUsed ? "Hint Used" : "Show Hint"}
              {loadingHint && (
                <span className="hint-spinner" style={{ marginLeft: "8px" }}>
                  <ClipLoader size={24} color="#ffffff" />
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {showBigOView && solutionCompleted ? (
        <BigOInfo
          isOptimal={isOptimal}
          timeComplexity={timeComplexity}
          spaceComplexity={spaceComplexity}
          handleLevelChange={handleLevelChange}
          problemName={problem?.title}
          codeSnippet={codeSnippet}
          handleCloseBigO={() => setShowBigOView(false)}
        />
      ) : (
        <div className={`block-library ${blockHighlightClass}`}>
          {level === "Pro" ? (
            <div className="pro-mode-message">
              🧱 No blocks in Pro mode — try solving on your own!
            </div>
          ) : (
            blocks?.map((block) => (
              <button
                key={block.id}
                className={`block-button ${block.type} ${
                  highlightedBlock === block.content ? "highlight" : ""
                }`}
                onClick={() => addToSolution(block.content)}
                title="Drag to Solution"
                draggable
                onDragStart={(e) => {
                  // Encode a payload so drop targets know this came from the library
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData(
                    "application/x-blockcode",
                    JSON.stringify({ source: "library", id: block.id })
                  );
                }}
                onDragEnd={() => {
                  // optional: no-op; native DnD already clears the ghost
                }}
              >
                {block.content}
              </button>
            ))
          )}
        </div>
      )}

      {!showBigOView && solutionCompleted && (
        <div className="show-big-o-container">
          <button
            className="show-big-o-button"
            onClick={() => setShowBigOView(true)}
          >
            Show Big O
          </button>
        </div>
      )}

      {solutionCompleted && (
        <div className="reset-blocks-container">
          <button className="reset-button" onClick={handleResetBlocks}>
            🔁 Reset Blocks & Retry
          </button>
        </div>
      )}

      {/* NEW: Legend Modal */}
      {showLegend && (
        <div
          className="legend-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLegend(false);
          }}
        >
          <div
            className="legend-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="legend-title"
          >
            <div className="legend-header">
              <h3 id="legend-title">Block Color Legend</h3>
              <button
                className="legend-close"
                aria-label="Close"
                onClick={() => setShowLegend(false)}
              >
                ×
              </button>
            </div>

            <ul className="legend-list">
              <li>
                <span className="legend-dot legend-blue" /> Variable
                declarations
              </li>
              <li>
                <span className="legend-dot legend-yellow" /> Loops
              </li>
              <li>
                <span className="legend-dot legend-orange" /> Conditionals
              </li>
              <li>
                <span className="legend-dot legend-red" /> Actions
              </li>
              <li>
                <span className="legend-dot legend-black" /> Return
              </li>
              <li>
                <span className="legend-dot legend-purple" /> Helper functions
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default BlocksSection;
